import * as React from "react";
import { locKeys } from "components/flexComponents/WizardCruisePWA/l10n";
import { UitkLayoutGridItem, UitkLayoutConditionalGridSpan } from "uitk-react-layout-grid";
import { observer } from "mobx-react";
import { useLocalization } from "@shared-ui/localization-context";
import { DESTINATION_GROUPS } from "./destinations";
import { Viewport, ViewSmall, ViewMedium, Layer } from "bernie-view";
import { UitkFakeInput } from "uitk-react-input";
import { CruiseWizardState } from "stores/wizard/state";
import { withStores } from "src/stores";
import { RouteStore, DialogRoute } from "bernie-core";
import { CruiseDestinationDialog } from "./CruiseDestinationDialog";
import { CruiseDestinationSelect } from "./CruiseDestinationSelect";

export interface CruiseDestinationProps {
  lobState: CruiseWizardState;
  colSpan: UitkLayoutConditionalGridSpan;
  routeStore: RouteStore;
}

const CruiseDestinationComponent = observer(({ lobState, colSpan, routeStore }: CruiseDestinationProps) => {
  const [selectName, setSelectName] = React.useState("destination");
  const [destinationForDisplay, setDestinationForDisplay] = React.useState("");
  const [useHiddenInputs, setUseHiddenInputs] = React.useState(false);
  const destinationDialogQueryRouteId = "cruise-destination";

  const openDestinationDialog = () => {
    routeStore.updateSearch({ pwaDialog: destinationDialogQueryRouteId });
  };

  const onDestinationDialogDismiss = () => {
    routeStore.goBack();
  };

  const onDestinationSelect = (destinationValue: string, newDestinationForDisplay: string) => {
    lobState.setCruiseDestination(destinationValue);
    setDestinationForDisplay(newDestinationForDisplay);
    routeStore.goBack();
  };

  const onDestinationSelectChange = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    lobState.setCruiseDestination(selectEvent.currentTarget.value);
  };

  /**
   * This hook is reached for rehydratation, so if JS is enabled there is no need of keep setting a name to UitkSelect component,
   * also we need to render the hidden input since it manages the select value, if JS is disabled then this hook is never executed
   */
  React.useEffect(() => {
    setSelectName("");
    setUseHiddenInputs(true);
  }, []);

  const { formatText } = useLocalization();

  return (
    <UitkLayoutGridItem colSpan={colSpan}>
      <div>
        <Viewport>
          <ViewSmall>
            <>
              <UitkFakeInput
                id="cruise-destination-trigger"
                className="cruise-destination-dialog-trigger"
                data-testid="cruise-destination-dialog-trigger"
                name={selectName}
                label={formatText(locKeys.destinationLabel)}
                placeholder={formatText(locKeys.destinationPlaceholder)}
                value={destinationForDisplay}
                onClick={openDestinationDialog}
                icon="place"
              />
              <DialogRoute id={destinationDialogQueryRouteId}>
                <Layer id={`${destinationDialogQueryRouteId}-dialog`}>
                  <CruiseDestinationDialog
                    onDismiss={onDestinationDialogDismiss}
                    onSave={onDestinationSelect}
                    queryRouteId={destinationDialogQueryRouteId}
                    destinationGroups={DESTINATION_GROUPS}
                  />
                </Layer>
              </DialogRoute>
            </>
          </ViewSmall>
          <ViewMedium>
            <CruiseDestinationSelect
              onChange={onDestinationSelectChange}
              name={selectName}
              value={lobState.cruiseDestination}
            />
          </ViewMedium>
        </Viewport>
        {useHiddenInputs && (
          <input
            type="hidden"
            data-testid="wizard-cruise-destination"
            name="destination"
            value={lobState.cruiseDestination}
          />
        )}
      </div>
    </UitkLayoutGridItem>
  );
});

export const CruiseDestination = withStores("routeStore")(CruiseDestinationComponent);
